<template>
    <div class="text-subtitle-1 pb-3">{{ $t('currency') }}: {{ formData.currency }}</div>
    <v-row no-gutters>
        <v-col cols="12" md="6">
            <div class="ma-1">
                <v-data-table
                    :headers="useHeaders"
                    :items="formData.loyalties"
                    disable-sort
                    density="compact"
                    class="elevation-1 mb-2"
                    :items-per-page="-1"
                    hide-default-footer
                >
                    <template v-slot:item.name="{item, index}">
                        <span class="text-link" :data-cy="`edit-${item.id}`" @click="editLoyalty(item, index)">
                            {{ getLoyaltyName(item.loyaltyId) }}
                        </span>
                        <div v-if="item.defaultContractPricingPlan" class="text-medium-emphasis">
                            ({{ $t('use_by_default') }})
                        </div>
                    </template>
                    <template v-slot:item.action="{item, index}">
                        <v-btn
                            v-if="!isReadOnly"
                            variant="text"
                            class="text-caption"
                            :data-cy="`delete-${item.id}`"
                            density="compact"
                            @click="deleteLoyalty(index)"
                        >
                            {{ $t('delete') }}
                        </v-btn>
                    </template>
                </v-data-table>
                <v-btn v-if="!isReadOnly" data-cy="show-modal" @click="showModal">
                    {{ $t('add') }}
                </v-btn>
            </div>
        </v-col>
    </v-row>
    <ModalWrapper v-model="isShowModal">
        <template v-slot:title> {{ isEditLoyalty ? $t('edit_loyalty_program') : $t('add_loyalty_program') }} </template>
        <template v-slot:content>
            <v-form v-model="valid" :disabled="isReadOnly">
                <v-autocomplete
                    v-model="selectedLoyalty.loyaltyId"
                    :items="activeLoyalties"
                    :label="`${$t('available_loyalty_programs')}*`"
                    item-value="id"
                    item-title="name"
                    clearable
                    :rules="[requiredRule('available_loyalty_programs')]"
                    data-cy="loyalty"
                    hide-details="auto"
                    class="mb-4"
                />
                <div class="text-subtitle-1 pb-3">{{ $t('select_currency_to_calculate_loyalty_points') }}</div>
                <v-select
                    v-model="selectedLoyalty.currency"
                    :items="useCurrency"
                    :label="$t('currency')"
                    :rules="[requiredRule('currency')]"
                    data-cy="currency"
                    max-width="200"
                    hide-details="auto"
                    class="mb-4"
                />
                <v-checkbox
                    v-model="selectedLoyalty.defaultContractPricingPlan"
                    hide-details
                    :label="$t('use_by_default')"
                    data-cy="use-by-default"
                />
                <div class="text-medium-emphasis pb-2">({{ $t('use_by_default_note') }})</div>
                <div class="pb-4">{{ $t('use_by_default_currency_note') }}</div>
                <div class="text-subtitle-1 font-weight-medium">{{ $t('payment_using_loyalty_points') }}</div>
                <div>{{ $t('specify_loyalty_points') }}</div>
                <div class="d-flex align-center flex-wrap gc-2 gr-1 pb-2">
                    <span>1 {{ $t('loyalty_points') }} =</span>
                    <v-number-input
                        v-model="selectedLoyalty.rate"
                        :min="0"
                        hide-details
                        :label="`${$t('amount')}*`"
                        width="160"
                        class="flex-grow-0 flex-shrink-0"
                        data-cy="rate"
                        :rules="[requiredRule('')]"
                    />
                    <span>{{ selectedLoyalty.currency }}</span>
                </div>
                <div class="d-flex align-center flex-wrap gc-2 gr-1">
                    <span>{{ $t('allow_to_pay_up_to') }}</span>
                    <v-number-input
                        v-model="selectedLoyalty.allowPayPartPercent"
                        :min="0"
                        hide-details
                        :label="`${$t('percent')}*`"
                        width="160"
                        class="flex-grow-0 flex-shrink-0"
                        data-cy="percent"
                        :rules="[requiredRule('')]"
                    />
                    <span>{{ $t('percent_with_loyalty_points') }}</span>
                </div>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn v-if="!isReadOnly" :disabled="!valid" variant="flat" data-cy="add" @click="addLoyalty">
                {{ $t('apply') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {useLoyalties} from '~/composables/loyalties'

    const {loyalties, activeLoyalties} = useLoyalties()

    const isShowModal = ref<boolean>(false)
    const isEditLoyalty = ref<boolean>(false)
    const selectedLoyaltyIndex = ref<number>(null)
    const valid = ref<boolean>(false)
    const selectedLoyalty = ref<any>(null)
    const formData = defineModel<any>()
    const {t} = useI18n()

    const isReadOnly = inject('isReadOnly', ref(false))

    const showModal = () => {
        selectedLoyalty.value = {
            loyaltyId: activeLoyalties.value?.[0]?.id || null,
            defaultContractPricingPlan: false,
            currency: formData.value.currency,
            allowPayPartPercent: null,
            rate: null,
        }
        isShowModal.value = true
    }

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('currency'), value: 'currency'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    const deleteLoyalty = index => {
        formData.value.loyalties.splice(index, 1)
    }

    const addLoyalty = () => {
        if (selectedLoyalty.value.defaultContractPricingPlan) {
            formData.value.loyalties.forEach(item => (item.defaultContractPricingPlan = false))
        }
        if (!isEditLoyalty.value) {
            formData.value.loyalties.push(selectedLoyalty.value)
        } else {
            isEditLoyalty.value = false
            formData.value.loyalties[selectedLoyaltyIndex.value] = selectedLoyalty.value
        }
        isShowModal.value = false
    }
    const editLoyalty = (loyalty, loyaltyIndex) => {
        selectedLoyalty.value = clone(loyalty)
        selectedLoyaltyIndex.value = loyaltyIndex
        isShowModal.value = true
        isEditLoyalty.value = true
    }

    const getLoyaltyName = loyaltyId => {
        const loyalty = loyalties.value.find(({id}) => id === loyaltyId)
        return loyalty?.name || ''
    }
</script>
